import { Fragment, useEffect, useMemo, type SyntheticEvent } from "react";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { useTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import isArray from "lodash/isArray";

import { useGetLocationsCached } from "../../../../pages/customer-settings/tabs/locations/hooks/useGetLocationsCached";
import type {
  AutocompleteOptionsType,
  AutocompleteOptionType,
  IDisableClearableOption,
} from "../../../models/autocomplete";

interface LocationIdSelectProps {
  setLocation: (locationValue: AutocompleteOptionType) => void;
  location: AutocompleteOptionType;
  defaultValue?: string;
  helperText?: string;
  setDefault?: boolean;
  disableClearable?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  label?: string;
  size?: "small" | "medium";
  defaultLocation?: AutocompleteOptionType;
}

const LocationIdSelect = ({
  setLocation,
  location,
  defaultValue,
  helperText,
  setDefault,
  disableClearable,
  hasError,
  disabled,
  label = "Select Location",
  size = "medium",
  defaultLocation,
}: LocationIdSelectProps): JSX.Element => {
  const { locations, loading } = useGetLocationsCached();

  useEffect((): void => {
    // Guard 1: Don't run until locations have loaded
    if (!isArray(locations) || !locations.length) {
      return;
    }

    // Guard 2: If location already has a valid value that exists in the list, preserve it
    if (location?.value && locations.some(l => l?.id === location.value)) {
      return;
    }

    // Now handle default-setting logic
    if (setDefault === false && !defaultValue) {
      setLocation(null);
    } else if (setDefault && defaultValue) {
      const locationItem = locations.find(l => l?.id === defaultValue) ?? null;

      setLocation({
        title: locationItem?.name ?? "",
        value: locationItem?.id ?? "",
      });
    } else if (setDefault) {
      setLocation({
        title: locations[0]?.name ?? "",
        value: locations[0]?.id ?? "",
      });
    } else if (defaultLocation) {
      setLocation(defaultLocation);
    } else {
      // Fallback: try to find current location in list, or use first
      const locationItem =
        locations.find(l => l?.id === location?.value) ?? null;

      if (!locationItem) {
        setLocation({
          title: locations[0]?.name ?? "",
          value: locations[0]?.id ?? "",
        });
      } else {
        setLocation({
          title: locationItem?.name ?? "",
          value: locationItem?.id ?? "",
        });
      }
    }
  }, [locations, setDefault, defaultValue, location]);

  const handleOnchange = (
    _event: SyntheticEvent<Element, Event>,
    optionValue: AutocompleteOptionType
  ): void => {
    if (!optionValue) {
      setLocation({
        title: "",
        value: "",
      });

      return;
    }

    setLocation(optionValue);
  };

  const options =
    useMemo<AutocompleteOptionsType>((): IDisableClearableOption[] => {
      const defaultOption = [
        {
          title: "",
          value: "",
        },
      ];

      if (!Array.isArray(locations)) {
        return defaultOption;
      }

      return locations?.map((location): IDisableClearableOption => {
        return {
          title: location?.name ?? "",
          value: location?.id ?? "",
        };
      });
    }, [locations]);

  return (
    <Autocomplete
      size={size}
      fullWidth
      disabled={disabled}
      disableClearable={disableClearable}
      loading={loading}
      options={options}
      getOptionLabel={(option: AutocompleteOptionType): string =>
        option?.title ?? ""
      }
      isOptionEqualToValue={(option, optionValue): boolean =>
        option?.value === "" ? true : option?.value === optionValue?.value
      }
      value={location}
      onChange={handleOnchange}
      popupIcon={<ExpandMoreOutlinedIcon />}
      renderOption={(props, option): JSX.Element => {
        return (
          <Fragment key={option?.value}>
            {option?.value && (
              <Box {...props} component="li">
                {option?.title}
              </Box>
            )}
          </Fragment>
        );
      }}
      renderInput={(params): JSX.Element => {
        const theme = useTheme();

        const isErrorAndNoValue = hasError && !location?.value;
        const errorStyles = isErrorAndNoValue
          ? {
              color: theme.palette.error.main,
            }
          : {};

        return (
          <TextField
            {...params}
            error={isErrorAndNoValue}
            helperText={
              helperText ?? (isErrorAndNoValue ? "This field is required" : "")
            }
            variant="outlined"
            label={label}
            fullWidth
            InputLabelProps={{
              style: errorStyles,
            }}
            FormHelperTextProps={{
              style: errorStyles,
            }}
            sx={{
              "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: theme => theme.palette.error.main,
                },
            }}
          />
        );
      }}
    />
  );
};

export default LocationIdSelect;
